import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ImagePlus, Link2, Loader2, Trash2 } from "lucide-react";
import type { CollectionItemsResponse, UrlMetadataRecord } from "lib/api";
import { urlMetadataApi } from "lib/api";
import { Button } from "ui/Button";
import { Card } from "ui/Card";
import { getUploadAccept, validateUploadFile } from "util/uploadValidation";
import { getCollectionUiStrings, resolveCollectionLocale } from "i18n/collectionUi";

export type LibraryDraftItem = LibraryDraftMediaItem | LibraryDraftUrlItem;

interface LibraryDraftItemBase {
  draftId: string;
  sortOrder: number;
}

export interface LibraryDraftMediaItem extends LibraryDraftItemBase {
  kind: "media";
  source: "existing" | "upload";
  entityId?: number;
  previewUrl: string;
  mediaType: string;
  fileName?: string | null;
  mimeType?: string | null;
  file?: File;
}

export interface LibraryDraftUrlItem extends LibraryDraftItemBase {
  kind: "url";
  source: "existing" | "resolved";
  entityId: number;
  url: string;
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  siteName?: string | null;
  host?: string | null;
}

interface LibraryCollectionFormProps {
  items: LibraryDraftItem[];
  onItemsChange: (items: LibraryDraftItem[]) => void;
  audience?: string;
  disabled?: boolean;
}

function createDraftId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `library-item-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeLibraryItems(items: LibraryDraftItem[]): LibraryDraftItem[] {
  return [...items]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((item, index) => ({
      ...item,
      sortOrder: index + 1,
    }));
}

function buildUploadMediaItem(file: File, sortOrder: number): LibraryDraftMediaItem | null {
  const validation = validateUploadFile(file, "media-manager");
  if (!validation.valid || !validation.category) {
    return null;
  }
  return {
    draftId: createDraftId(),
    kind: "media",
    source: "upload",
    sortOrder,
    previewUrl: URL.createObjectURL(file),
    mediaType: validation.category,
    fileName: file.name,
    mimeType: file.type,
    file,
  };
}

function mapUrlMetadataToDraftItem(
  metadata: UrlMetadataRecord,
  sortOrder: number,
  source: "existing" | "resolved"
): LibraryDraftUrlItem | null {
  if (!metadata?.id || typeof metadata.url !== "string" || metadata.url.trim().length === 0) {
    return null;
  }
  return {
    draftId: createDraftId(),
    kind: "url",
    source,
    sortOrder,
    entityId: metadata.id,
    url: metadata.url,
    title: metadata.title ?? null,
    description: metadata.description ?? null,
    imageUrl: metadata.image_url ?? null,
    siteName: metadata.site_name ?? null,
    host: metadata.host ?? null,
  };
}

function buildExistingMediaItem(
  item: Record<string, unknown>,
  sortOrder: number
): LibraryDraftMediaItem | null {
  const itemId = Number.parseInt(String(item.id ?? ""), 10);
  const previewUrl =
    (typeof item.signed_url === "string" && item.signed_url) ||
    (typeof item.url === "string" && item.url) ||
    "";
  if (!Number.isFinite(itemId) || itemId <= 0 || previewUrl.length === 0) {
    return null;
  }
  return {
    draftId: createDraftId(),
    kind: "media",
    source: "existing",
    sortOrder,
    entityId: itemId,
    previewUrl,
    mediaType: typeof item.media_type === "string" ? item.media_type : "image",
    fileName: typeof item.file_name === "string" ? item.file_name : null,
    mimeType: typeof item.mime_type === "string" ? item.mime_type : null,
  };
}

export function mapCollectionItemsToLibraryDraftItems(
  payload: CollectionItemsResponse
): LibraryDraftItem[] {
  const relations = Array.isArray(payload.item_relations) ? payload.item_relations : [];
  const items = Array.isArray(payload.items) ? payload.items : [];
  const draftItems = items.reduce<LibraryDraftItem[]>((accumulator, item, index) => {
    const relation = relations[index];
    const relationSortOrder = index + 1;
    if (relation?.related_type === "media_asset") {
      const mediaItem = buildExistingMediaItem(item, relationSortOrder);
      if (mediaItem) {
        accumulator.push(mediaItem);
      }
      return accumulator;
    }
    if (relation?.related_type === "url_metadata") {
      const urlItem = mapUrlMetadataToDraftItem(item as UrlMetadataRecord, relationSortOrder, "existing");
      if (urlItem) {
        accumulator.push(urlItem);
      }
      return accumulator;
    }
    return accumulator;
  }, []);
  return normalizeLibraryItems(draftItems);
}

const LibraryCollectionForm: React.FC<LibraryCollectionFormProps> = ({
  items,
  onItemsChange,
  audience = "public",
  disabled = false,
}) => {
  const ui = getCollectionUiStrings(resolveCollectionLocale());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewUrlsRef = useRef<Set<string>>(new Set());
  const [urlInput, setUrlInput] = useState("");
  const [isResolvingUrl, setIsResolvingUrl] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const currentPreviewUrls = new Set(
      items
        .filter((item): item is LibraryDraftMediaItem => item.kind === "media" && item.source === "upload")
        .map((item) => item.previewUrl)
    );

    previewUrlsRef.current.forEach((previewUrl) => {
      if (!currentPreviewUrls.has(previewUrl)) {
        URL.revokeObjectURL(previewUrl);
      }
    });

    previewUrlsRef.current = currentPreviewUrls;
  }, [items]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
      });
      previewUrlsRef.current.clear();
    };
  }, []);

  const sortedItems = useMemo(() => normalizeLibraryItems(items), [items]);

  const commitItems = (nextItems: LibraryDraftItem[]) => {
    onItemsChange(normalizeLibraryItems(nextItems));
  };

  const handleSelectedFiles = (fileList: FileList | null) => {
    if (!fileList || disabled) {
      return;
    }

    const nextItems: LibraryDraftItem[] = [...sortedItems];
    const rejectedMessages: string[] = [];

    Array.from(fileList).forEach((file) => {
      const validation = validateUploadFile(file, "media-manager");
      if (!validation.valid || !validation.category) {
        rejectedMessages.push(`${file.name} (${validation.message})`);
        return;
      }
      const mediaItem = buildUploadMediaItem(file, nextItems.length + 1);
      if (mediaItem) {
        nextItems.push(mediaItem);
      }
    });

    if (rejectedMessages.length > 0) {
      setErrorMessage(rejectedMessages.join(", "));
    } else {
      setErrorMessage("");
    }

    commitItems(nextItems);
  };

  const handleMove = (draftId: string, direction: "up" | "down") => {
    const currentIndex = sortedItems.findIndex((item) => item.draftId === draftId);
    if (currentIndex < 0) {
      return;
    }
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= sortedItems.length) {
      return;
    }
    const nextItems = [...sortedItems];
    const [movedItem] = nextItems.splice(currentIndex, 1);
    nextItems.splice(targetIndex, 0, movedItem);
    commitItems(nextItems);
  };

  const handleRemove = (draftId: string) => {
    commitItems(sortedItems.filter((item) => item.draftId !== draftId));
  };

  const handleAddUrl = async () => {
    if (disabled || isResolvingUrl) {
      return;
    }

    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      return;
    }

    let parsedUrl: URL;
    try {
      parsedUrl = new URL(trimmedUrl);
    } catch {
      setErrorMessage(ui.invalidHttpsUrl);
      return;
    }

    if (parsedUrl.protocol !== "https:") {
      setErrorMessage(ui.invalidHttpsUrl);
      return;
    }

    setErrorMessage("");
    setIsResolvingUrl(true);
    try {
      const metadata = await urlMetadataApi.resolve(parsedUrl.toString(), {
        audience,
      });
      const nextItem = mapUrlMetadataToDraftItem(metadata, sortedItems.length + 1, "resolved");
      if (!nextItem) {
        setErrorMessage(ui.linkResolveError);
        return;
      }
      commitItems([...sortedItems, nextItem]);
      setUrlInput("");
    } catch {
      setErrorMessage(ui.linkResolveError);
    } finally {
      setIsResolvingUrl(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
          {ui.libraryItemsTitle}
        </h3>
        <p className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {ui.libraryItemsSubtitle}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getUploadAccept("media-manager")}
          className="hidden"
          onChange={(event) => {
            handleSelectedFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="rounded-full"
        >
          <ImagePlus aria-hidden />
          {ui.addMedia}
        </Button>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
          {ui.linkUrlLabel}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="url"
            inputMode="url"
            value={urlInput}
            onChange={(event) => setUrlInput(event.target.value)}
            placeholder={ui.linkUrlPlaceholder}
            className="w-full rounded-lg border border-eoe-border-main bg-eoe-bg px-3 py-2 text-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
            disabled={disabled || isResolvingUrl}
          />
          <Button
            type="button"
            onClick={() => void handleAddUrl()}
            disabled={disabled || isResolvingUrl || urlInput.trim().length === 0}
            className="rounded-full"
          >
            {isResolvingUrl ? <Loader2 className="animate-spin" aria-hidden /> : <Link2 aria-hidden />}
            {ui.addLink}
          </Button>
        </div>
        {isResolvingUrl ? (
          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.linkResolving}</p>
        ) : null}
      </div>

      {errorMessage ? (
        <div className="text-sm text-warning-red dark:text-warning-red-dark" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {sortedItems.length === 0 ? (
        <Card padding="sm" className="border-dashed">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.libraryEmpty}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedItems.map((item, index) => (
            <Card key={item.draftId} padding="sm" className="overflow-hidden">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex min-w-0 flex-1 gap-3">
                  <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-eoe-icon-hover/30 dark:bg-eoe-dark-icon-hover/30">
                    {item.kind === "media" ? (
                      item.mediaType === "video" ? (
                        <video
                          src={item.previewUrl}
                          className="h-full w-full object-cover"
                          muted
                          playsInline
                          controls
                        />
                      ) : (
                        <img
                          src={item.previewUrl}
                          alt={item.fileName || ui.mediaItemLabel}
                          className="h-full w-full object-cover"
                        />
                      )
                    ) : item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title || ui.linkItemLabel}
                        className="h-full w-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <Link2 className="size-5 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {item.kind === "media"
                        ? item.fileName || `${ui.mediaItemLabel} ${index + 1}`
                        : item.title || item.url}
                    </div>
                    <div className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {item.kind === "media"
                        ? item.mimeType || item.mediaType
                        : item.siteName || item.host || item.url}
                    </div>
                    {item.kind === "url" && item.description ? (
                      <p className="mt-2 line-clamp-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex shrink-0 items-start gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={ui.moveUp}
                    onClick={() => handleMove(item.draftId, "up")}
                    disabled={disabled || index === 0}
                  >
                    <ChevronUp aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={ui.moveDown}
                    onClick={() => handleMove(item.draftId, "down")}
                    disabled={disabled || index === sortedItems.length - 1}
                  >
                    <ChevronDown aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={ui.removeItem}
                    onClick={() => handleRemove(item.draftId)}
                    disabled={disabled}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LibraryCollectionForm;
